<template>
  <SfDropdown
    data-testid="note-types-filter-dropdown"
    :is-open="isScopedDropdownOpen"
    :dropdown-id="dropdownId"
    @dropdown-clicked="handleToggleDropdown"
    @dropdown-closed="dropdownStore.closeDropdown"
  >
    <template #trigger>
      <div class="tw-max-w-[200px]">
        <span>{{ triggerDisplayValue }}</span>
      </div>
    </template>

    <template #menu>
      <div
        v-for="(group, index) in options"
        :key="group.id"
        class="tw-min-w-[150px]"
      >
        <SfDropdownItemGroup :with-separator="index !== options.length - 1">
          <SfDropdownItem
            v-if="index === 0"
            :is-selected="selectedNoteType === null"
            :has-border="false"
            :class="{ 'tw-font-medium': selectedNoteType === null }"
            @click="clearSelected"
          >
            {{ I18n.t('inbox.filters.note_types.labels.any') }}
          </SfDropdownItem>
          <SfDropdownItem
            v-for="option in group.options"
            :key="option.value"
            class="sf-dropdown-item tw-flex tw-items-center"
            :is-wrapped="false"
            :has-border="false"
            :is-selected="selectedNoteType === option"
            :class="{ 'tw-font-medium': selectedNoteType === option }"
            @click="selectNoteType(option)"
          >
            <span
              v-if="option.icon"
              :class="`fa-${option.icon} tw-text-${option.iconColor}`"
              class="tw-mr-3 fa"
            />
            <span>{{ option.label }}</span>
          </SfDropdownItem>
        </SfDropdownItemGroup>
      </div>
    </template>
  </SfDropdown>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useInboxFiltersStore } from '../store/inbox-filters'
import { useDropdown } from '@app/shared/pinia/dropdown'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfDropdownItemGroup from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item-group.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'

interface Option {
  label: string
  value: number | string
  icon?: string
  iconColor?: string
}

interface OptionGroup {
  id: string
  options: Option[]
}

interface NoteTypesFilterProps {
  options?: OptionGroup[]
  dropdownId?: string
}

const props = withDefaults(defineProps<NoteTypesFilterProps>(), {
  options: () => [],
  dropdownId: 'noteTypeInboxFilter',
})

const I18n = useI18n()
const inboxFiltersStore = useInboxFiltersStore()
const dropdownStore = useDropdown()

const { currentDropdownId } = storeToRefs(dropdownStore)

const selectedNoteType = computed(() => {
  return (
    props.options
      .flatMap((g) => g.options)
      .find((option) => option.value === inboxFiltersStore.noteType) || null
  )
})

const triggerDisplayValue = computed(() => {
  const dropdownTitle = I18n.t('inbox.filters.note_types.dropdown_title')

  if (selectedNoteType.value?.label) {
    return `${dropdownTitle}: ${selectedNoteType.value.label}`
  }

  return `${dropdownTitle}: ${I18n.t('inbox.filters.note_types.labels.any')}`
})

const isScopedDropdownOpen = computed(() => {
  return currentDropdownId.value === props.dropdownId
})

function handleToggleDropdown() {
  dropdownStore.toggleDropdown(props.dropdownId)
}

function selectNoteType(option: Option): void {
  if (inboxFiltersStore.noteType !== option.value) {
    inboxFiltersStore.noteType = option.value
    handleToggleDropdown()
  } else {
    clearSelected()
  }
}

function clearSelected(): void {
  inboxFiltersStore.noteType = null
  handleToggleDropdown()
}
</script>
